import {
  Navigate,
  Outlet,
  createBrowserRouter,
  redirect,
} from "react-router-dom";
import { GraphicsRoute } from "~graphics/graphics.route";
import { RootRouteError } from "~common/root-route-error";
import { ProtectedRoute } from "~common/protected-route";
import { PageLayout } from "~common/page-layout";
import { restoreSearchParamsLoader } from "~utils/url";
import { RootRoute } from "./root";
import { DeviceStatusProvider } from "../device/device-status-context";
import { PointEnergyReportStateProvider } from "../point/point-energy-reports/point-energy-report-state-context";

/**
 * This is the way to define routes with React Router v6.4 and higher.
 * It enables the data APIs like loaders, actions, fetchers and more.
 * See: https://reactrouter.com/en/main/routers/create-browser-router
 */
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRoute />,
    errorElement: <RootRouteError />,
    children: [
      /**
       * We dont' render anything in the root route `/` so we want to redirect
       * the user to the protected `/company` url segment that will handle the
       * automatic redirect to the first available company if the user is authorized.
       */
      {
        index: true,
        element: <Navigate to="company" replace />,
      },
      {
        path: "auth/callback",
        lazy: () => import("./authenticated-callback"),
      },
      {
        path: "auth/unauthorized",
        lazy: () => import("./unauthorized"),
      },
      {
        path: "logout",
        lazy: () => import("./logout"),
      },
      /**
       * All the main routes of the app are under the `/company` url segment.
       */
      {
        path: "company",
        element: <ProtectedRoute />,
        children: [
          {
            path: ":companyId",
            element: (
              <DeviceStatusProvider>
                <PageLayout />
              </DeviceStatusProvider>
            ),
            children: [
              /**
               * Redirect to `/overview` if user lands on `/company/:companyId`
               * since that is the "home" page of the app.
               */
              {
                index: true,
                loader: ({ request, params }) => {
                  /* Must be a loader redirect, Issue with `<Navigate>` is `ProtectedRoute`
                   * returns null until the user is loaded, so an index element
                   * never mounts on a cold load of this URL.
                   */
                  const url = new URL(request.url);
                  return redirect(
                    `/company/${params.companyId}/overview${url.search}`,
                  );
                },
              },
              {
                path: "dashboard",
                lazy: () => import("../dashboard/dashboard.route"),
              },
              {
                path: "points",
                lazy: () => import("../point/point-list/point-list.route"),
                loader: restoreSearchParamsLoader,
              },
              {
                path: "history",
                lazy: () =>
                  import("../point/point-history/point-history.route"),
              },
              {
                path: "energy",
                element: (
                  <PointEnergyReportStateProvider>
                    <Outlet />
                  </PointEnergyReportStateProvider>
                ),
                children: [
                  {
                    path: "",
                    lazy: () =>
                      import(
                        "../point/point-energy-reports/point-energy.route"
                      ),
                  },
                  {
                    path: "save/:reportId?",
                    lazy: () =>
                      import(
                        "../point/point-energy-reports/point-energy-save.route"
                      ),
                  },
                ],
              },
              {
                path: "devices/:deviceId/points/:pointId",
                lazy: () =>
                  import("../point/point-details/point-details.route"),
              },
              {
                path: "devices/:deviceId/points/:pointId/edit",
                lazy: () => import("../schedule/edit-point.route"),
              },
              // Point Groups
              {
                path: "pointgroups/create",
                lazy: () =>
                  import("../pointgroup/routes/point-group-create.route"),
              },
              {
                path: "pointgroups/edit",
                lazy: () =>
                  import("../pointgroup/routes/point-group-edit.route"),
              },
              // Alarms
              {
                path: "alarms",
                lazy: () => import("../alarm/alarm-list/alarm-list.route"),
                loader: (args) =>
                  restoreSearchParamsLoader({
                    ...args,
                    defaultParams: "tab=active&sort=lastUpdate&order=desc",
                  }),
              },
              {
                path: "devices/:deviceId/alarms/:pointId",
                lazy: () =>
                  import("../point/point-details/point-details.route"),
              },
              // Devices
              {
                path: `devices`,
                lazy: () => import("../device/device-list/device-list.route"),
                loader: restoreSearchParamsLoader,
              },
              // overview
              {
                path: `overview`,
                lazy: () => import("../overview/overview-route"),
                loader: restoreSearchParamsLoader,
                handle: { excludeGlobalSearchParams: true },
              },
              /**
               * calendar and schedule
               */
              {
                path: `schedule`,
                children: [
                  {
                    path: "calendars",
                    lazy: () =>
                      import("../calendar-point/calendar-point-list.route"),
                    loader: restoreSearchParamsLoader,
                  },
                  {
                    path: "calendars/:pointId/create",
                    lazy: () =>
                      import("../calendar-point/create-calendar-event"),
                  },
                  {
                    path: "timeschedules",
                    lazy: () =>
                      import(
                        "../schedule/time-schedule/time-schedule-list.route"
                      ),
                    loader: restoreSearchParamsLoader,
                  },
                ],
              },

              // Graphics
              {
                path: "graphics",
                element: <GraphicsRoute />,
                loader: restoreSearchParamsLoader,
              },
              {
                path: "graphics/:levelId/add-graphics",
                lazy: () => import("../graphics/add-level-graphics.route"),
              },
              /**
               * event and alarm logs
               */
              {
                path: "logs/:logType",
                lazy: () => import("../logs/log-list.route"),
                loader: restoreSearchParamsLoader,
              },

              /**
               * This catch-all route is rendered if none of the above routes match.
               * It's also matched transiently for bare `/company/:companyId` URLs
               * (eg. right after switching company) before being redirected
               * elsewhere, so it must not pick up any global search params.
               */
              {
                path: "*",
                lazy: () => import("./not-found"),
                handle: { excludeGlobalSearchParams: true },
              },
            ],
          },
        ],
      },
      /**
       * This catch-all route is rendered if none of the above routes match
       */
      {
        path: "*",
        lazy: () => import("./not-found"),
      },
    ],
  },
]);
